import { useState, useEffect } from 'react';
import axios from 'axios';

const INDUSTRIES = [
  { value: '', label: 'Select an industry' },
  { value: 'Agriculture, Forestry, Fishing and Hunting', label: 'Agriculture, Forestry & Fishing' },
  { value: 'Mining and Oil and Gas Extraction', label: 'Mining & Energy Extraction' },
  { value: 'Utilities', label: 'Utilities' },
  { value: 'Construction', label: 'Construction' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Wholesale Trade', label: 'Wholesale Trade' },
  { value: 'Retail Trade', label: 'Retail Trade' },
  { value: 'Transportation and Warehousing', label: 'Transportation & Logistics' },
  { value: 'Information and Cultural Industries', label: 'Media & Information' },
  { value: 'Finance and insurance', label: 'Finance & Insurance' },
  { value: 'Real Estate and Rental and Leasing', label: 'Real Estate' },
  { value: 'Professional, Scientific and Technical Services', label: 'Professional Services' },
  { value: 'Management of Companies and Enterprises', label: 'Corporate Management' },
  { value: 'Educational Services', label: 'Education' },
  { value: 'Health Care and Social Assistance', label: 'Healthcare' },
  { value: 'Arts, Entertainment and Recreation', label: 'Arts & Entertainment' },
  { value: 'Accommodation and Food Services', label: 'Hospitality' },
  { value: 'Other Services (except Public Administration)', label: 'Other Services' }
];

const NEWS_TYPES = [
  { value: 'b2b', label: 'Business Intelligence' },
  { value: 'trends', label: 'Market Trends' },
  { value: 'competition', label: 'Competitive Analysis' },
  { value: 'innovation', label: 'Innovation & Technology' },
  { value: 'regulation', label: 'Regulatory & Compliance' }
];

const REGIONS = [
  { value: 'Alberta', label: 'Alberta' },
  { value: 'British Columbia', label: 'British Columbia' },
  { value: 'Manitoba', label: 'Manitoba' },
  { value: 'New Brunswick', label: 'New Brunswick' },
  { value: 'Newfoundland and Labrador', label: 'Newfoundland & Labrador' },
  { value: 'Northwest Territories', label: 'Northwest Territories' },
  { value: 'Nova Scotia', label: 'Nova Scotia' },
  { value: 'Nunavut', label: 'Nunavut' },
  { value: 'Ontario', label: 'Ontario' },
  { value: 'Prince Edward Island', label: 'Prince Edward Island' },
  { value: 'Quebec', label: 'Quebec' },
  { value: 'Saskatchewan', label: 'Saskatchewan' },
  { value: 'Yukon', label: 'Yukon' },
  { value: 'Canada (Nationwide)', label: 'Canada (All Regions)' }
];

export default function ReportForm({ onReportGenerated, onGeneratingStart }) {
  const [industries, setIndustries] = useState([]);
  const [businessCategories, setBusinessCategories] = useState([]);
  const [formData, setFormData] = useState({
    industry: '',
    industryTypeId: '',
    businessCategoryId: '',
    newsType: 'b2b',
    keywords: '',
    region: 'Quebec'
  });
  const [loadingIndustries, setLoadingIndustries] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchIndustries() {
      try {
        const response = await axios.get('/api/reports/industry-types');
        setIndustries([{ id: '', name: 'Select an industry' }, ...response.data.industries]);
      } catch (error) {
        console.error('Failed to load industries:', error);
      } finally {
        setLoadingIndustries(false);
      }
    }
    fetchIndustries();
  }, []);

  useEffect(() => {
    async function fetchCategories() {
      if (!formData.industryTypeId) {
        setBusinessCategories([]);
        return;
      }
      
      setLoadingCategories(true);
      try {
        const response = await axios.get(`/api/reports/business-categories?industryTypeId=${formData.industryTypeId}`);
        setBusinessCategories([{ id: '', name: 'All categories' }, ...response.data.categories]);
        setFormData(prev => ({ ...prev, businessCategoryId: '' }));
      } catch (error) {
        console.error('Failed to load categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    }
    fetchCategories();
  }, [formData.industryTypeId]);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setGenerating(true);
    setError(null);

    try {
      const industryObj = industries.find(i => i.id === formData.industryTypeId);
      const payload = {
        industry: industryObj ? industryObj.name : 'General',
        industryTypeId: formData.industryTypeId || null,
        businessCategoryId: (formData.businessCategoryId && formData.businessCategoryId !== '') ? parseInt(formData.businessCategoryId) : null,
        newsType: formData.newsType,
        keywords: formData.keywords,
        region: formData.region
      };

      // Notify parent that generation started
      const tempId = 'generating-' + Date.now();
      if (onGeneratingStart) {
        onGeneratingStart(tempId);
      }

      // 增加超时时间到 10 分钟
      const response = await axios.post('/api/reports/generate', payload, {
        timeout: 600000, // 10 minutes
      });
      
      // Update with real report ID
      if (response.data.reportId && onGeneratingStart) {
        onGeneratingStart(response.data.reportId);
      }

      if (response.data.success) {
        onReportGenerated(response.data);
        setFormData({
          industry: '',
          industryTypeId: '',
          businessCategoryId: '',
          newsType: 'b2b',
          keywords: '',
          region: 'Quebec'
        });
      } else {
        setError(response.data.error || 'Failed to generate report');
      }
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('Report generation is taking longer than expected. Please try again in a few minutes.');
      } else {
        setError(err.response?.data?.error || 'Network error. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          Generate Intelligence Report
        </h2>
        <p className="text-sm text-slate-600">
          Configure parameters for your custom market analysis
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {/* Industry Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Industry Sector
            </label>
            {loadingIndustries ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <div className="h-4 w-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : (
              <select
                name="industryTypeId"
                value={formData.industryTypeId}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-all bg-white"
                required
              >
                {industries.map(ind => (
                  <option key={ind.id} value={ind.id}>
                    {ind.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Business Category
            </label>
            {loadingCategories ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <div className="h-4 w-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : (
              <select
                name="businessCategoryId"
                value={formData.businessCategoryId}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-all bg-white disabled:bg-slate-50 disabled:text-slate-400"
                disabled={!formData.industryTypeId}
              >
                {businessCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* News Type Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Analysis Type
          </label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {NEWS_TYPES.map(type => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, newsType: type.value }))}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                  formData.newsType === type.value
                    ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Keywords Input */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Search Keywords
          </label>
          <input
            type="text"
            name="keywords"
            value={formData.keywords}
            onChange={handleChange}
            placeholder="e.g., yoga studio, wellness, fitness training"
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-all"
            required
          />
          <p className="mt-2 text-xs text-slate-500">
            Enter specific terms to focus the analysis on relevant market segments
          </p>
        </div>

        {/* Region Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Geographic Region
          </label>
          <select
            name="region"
            value={formData.region}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-all bg-white"
            required
          >
            {REGIONS.map(region => (
              <option key={region.value} value={region.value}>
                {region.label}
              </option>
            ))}
          </select>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            ⚠️ {error}
          </div>
        )}

        {/* Submit Button */}
        <div className="pt-4 border-t border-slate-200">
          <button
            type="submit"
            disabled={generating || !formData.industryTypeId}
            className={`w-full py-3.5 px-6 rounded-lg text-sm font-medium transition-all ${
              generating || !formData.industryTypeId
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-slate-900 text-white hover:bg-slate-800 shadow-sm hover:shadow-md'
            }`}
          >
            {generating ? (
              <span className="flex items-center justify-center gap-3">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <div className="flex flex-col items-start">
                  <span>Generating Your Report...</span>
                  <span className="text-xs opacity-80 font-normal">This typically takes 2-5 minutes</span>
                </div>
              </span>
            ) : (
              'Generate Intelligence Report'
            )}
          </button>
          {generating && (
            <p className="text-xs text-slate-500 text-center mt-3">
              ⏱️ Please keep this window open. You'll be notified when the report is ready.
            </p>
          )}
        </div>
      </form>
    </div>
  );
}
